import { useLayoutEffect, useRef } from "react";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS can report as Mac
    (navigator.userAgent.includes("Mac") && "ontouchend" in document);
}

function isTextInput(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "textarea") return true;
  if (tag === "input") {
    const type = (el.getAttribute("type") || "text").toLowerCase();
    // treat most input types as text-ish; exclude buttons etc.
    return ![
      "button",
      "submit",
      "reset",
      "checkbox",
      "radio",
      "range",
      "color",
      "file",
      "image",
      "hidden",
    ].includes(type);
  }
  if (tag === "select") return true;
  // contenteditable
  if (el.isContentEditable) return true;
  return false;
}

function isKeyboardLikelyOpen() {
  // visualViewport is the most reliable signal on mobile
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  if (!vv) return false;
  // When the keyboard opens, vv.height shrinks. Use a conservative threshold.
  const diff = window.innerHeight - vv.height;
  return diff > 80;
}

const lockState = {
  tokens: new Map(),
  snapshot: null,
  currentMode: null, // "soft" | "hard" | null
  hardLockScrollY: null,
  touchBlocker: null,
};

function getDomRefs() {
  if (typeof document === "undefined") return {};
  return {
    html: document.documentElement,
    body: document.body,
    modalRoot: document.getElementById("modal-root"),
    globalModal: document.querySelector(".global-message-modal"),
    bottomNav: document.getElementById("bottom-nav"),
    sideNav: document.querySelector(".side-navigation, #side-nav"),
  };
}

function ensureSnapshot() {
  if (lockState.snapshot) return lockState.snapshot;

  const { html, body, modalRoot, globalModal, bottomNav, sideNav } = getDomRefs();
  if (!html || !body) return null;

  lockState.snapshot = {
    htmlOverflow: html.style.overflow,
    bodyPosition: body.style.position,
    bodyTop: body.style.top,
    bodyLeft: body.style.left,
    bodyRight: body.style.right,
    bodyOverflow: body.style.overflow,
    bodyWidth: body.style.width,
    bodyOverscroll: body.style.overscrollBehavior,
    bodyPointerEvents: body.style.pointerEvents,
    modalRootPointer: modalRoot?.style.pointerEvents ?? "",
    globalModalPointer: globalModal?.style.pointerEvents ?? "",
    bottomNavPointer: bottomNav?.style.pointerEvents ?? "",
    sideNavPointer: sideNav?.style.pointerEvents ?? "",
  };

  return lockState.snapshot;
}

function ensureTouchBlocker(enabled) {
  if (typeof window === "undefined") return;

  if (enabled) {
    if (!lockState.touchBlocker) {
      lockState.touchBlocker = (e) => e.preventDefault();
      window.addEventListener("touchmove", lockState.touchBlocker, { passive: false });
    }
    return;
  }

  if (lockState.touchBlocker) {
    window.removeEventListener("touchmove", lockState.touchBlocker);
    lockState.touchBlocker = null;
  }
}

function applySoftLock() {
  const snapshot = ensureSnapshot();
  if (!snapshot) return;

  const prevMode = lockState.currentMode;
  const { html, body } = getDomRefs();
  if (!html || !body) return;

  // Soft lock must undo possible hard-lock body fixing first.
  body.style.position = snapshot.bodyPosition;
  body.style.top = snapshot.bodyTop;
  body.style.left = snapshot.bodyLeft;
  body.style.right = snapshot.bodyRight;
  body.style.width = snapshot.bodyWidth;
  body.style.pointerEvents = snapshot.bodyPointerEvents;

  html.style.overflow = "hidden";
  body.style.overflow = "hidden";
  body.style.overscrollBehavior = "contain";

  ensureTouchBlocker(false);

  // If we were in hard mode before, restore viewport position while keeping lock active.
  if (prevMode === "hard" && lockState.hardLockScrollY != null) {
    window.scrollTo(0, lockState.hardLockScrollY);
  }

  lockState.currentMode = "soft";
}

function applyHardLock() {
  const snapshot = ensureSnapshot();
  if (!snapshot) return;

  const { html, body } = getDomRefs();
  if (!html || !body) return;

  if (lockState.currentMode !== "hard") {
    lockState.hardLockScrollY = window.scrollY;
  }

  body.style.position = "fixed";
  body.style.top = `-${lockState.hardLockScrollY}px`;
  body.style.left = "0";
  body.style.right = "0";
  body.style.overflow = "hidden";
  body.style.width = "100%";
  body.style.overscrollBehavior = "contain";
  html.style.overflow = "hidden";

  // iOS Safari / in-app webviews can freeze taps when we force pointer-events
  // on body/modal layers or install global touchmove preventDefault blockers.
  // Keep only scroll lock mechanics here.
  ensureTouchBlocker(false);
  lockState.currentMode = "hard";
}

function restoreSnapshot() {
  const snapshot = lockState.snapshot;
  const { html, body, modalRoot, globalModal, bottomNav, sideNav } = getDomRefs();

  ensureTouchBlocker(false);

  if (snapshot && html && body) {
    html.style.overflow = snapshot.htmlOverflow;
    body.style.position = snapshot.bodyPosition;
    body.style.top = snapshot.bodyTop;
    body.style.left = snapshot.bodyLeft;
    body.style.right = snapshot.bodyRight;
    body.style.overflow = snapshot.bodyOverflow;
    body.style.width = snapshot.bodyWidth;
    body.style.overscrollBehavior = snapshot.bodyOverscroll;
    body.style.pointerEvents = snapshot.bodyPointerEvents;

    if (modalRoot) modalRoot.style.pointerEvents = snapshot.modalRootPointer;
    if (globalModal) globalModal.style.pointerEvents = snapshot.globalModalPointer;
    if (bottomNav) bottomNav.style.pointerEvents = snapshot.bottomNavPointer;
    if (sideNav) sideNav.style.pointerEvents = snapshot.sideNavPointer;
  }

  if (lockState.hardLockScrollY != null) {
    window.scrollTo(0, lockState.hardLockScrollY);
  }

  lockState.snapshot = null;
  lockState.currentMode = null;
  lockState.hardLockScrollY = null;
}

function syncGlobalLock() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  if (lockState.tokens.size === 0) {
    restoreSnapshot();
    return;
  }

  const allSoft = Array.from(lockState.tokens.values()).every((meta) => meta.soft);
  if (allSoft) {
    applySoftLock();
  } else {
    applyHardLock();
  }
}

function acquireLock(token, soft) {
  lockState.tokens.set(token, { soft });
  syncGlobalLock();
}

function releaseLock(token) {
  if (lockState.tokens.delete(token)) {
    syncGlobalLock();
  }
}

export function useBodyLock(locked, options = {}) {
  const tokenRef = useRef(Symbol("body-lock"));

  useLayoutEffect(() => {
    if (!locked) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // iOS Safari/WebView can collapse keyboard during hard-lock.
    // For text input contexts prefer soft-lock.
    const activeEl = document.activeElement;
    // iOS Safari / in-app webviews are much more stable with soft lock.
    // Hard lock (body fixed + aggressive handlers) is a frequent source of
    // frozen taps in modals after viewport/keyboard transitions.
    const shouldSoftLock =
      Boolean(options?.preferSoftLock) ||
      isIOS() ||
      (isTextInput(activeEl) || isKeyboardLikelyOpen());
    const token = tokenRef.current;
    acquireLock(token, shouldSoftLock);

    return () => {
      releaseLock(token);
    };
  }, [locked, options?.preferSoftLock]);
}
